import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories, articles, faqs, testimonials } from '@/mocks/data';

function HomeSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '男人团——男性成长社群官方平台',
    description: '男人团是专为现代男性打造的成长型社群平台，聚焦男性健康、事业、情感与自我提升。',
    url: 'https://nantuan.com',
    isPartOf: {
      '@type': 'WebSite',
      name: '男人团',
      url: 'https://nantuan.com',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
    </>
  );
}

function Hero() {
  return (
    <section className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Dark%20atmospheric%20abstract%20background%20with%20warm%20golden%20and%20amber%20accents%2C%20masculine%20strength%20and%20determination%20theme%2C%20subtle%20geometric%20patterns%2C%20elegant%20dark%20gradient%2C%20luxurious%20texture%2C%20dramatic%20lighting%2C%20no%20text%2C%20clean%20sophisticated%20composition&width=1920&height=1080&seq=home-hero-bg&orientation=landscape"
          alt="男人团"
          className="w-full h-full object-cover object-top"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>

      <div className="relative z-10 w-full max-w-4xl mx-auto px-4 text-center">
        <div className="mb-6">
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary-500/20 text-primary-300 text-sm font-medium border border-primary-500/30 backdrop-blur-sm">
            男性专属成长社群
          </span>
        </div>
        <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
          突破自我
          <br />
          <span className="text-primary-400">成为更好的男人</span>
        </h1>
        <p className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
          男人团是专为现代男性打造的成长型社群平台，聚焦<strong className="text-white">男性健康</strong>、<strong className="text-white">事业突破</strong>、<strong className="text-white">情感关系</strong>与<strong className="text-white">自我提升</strong>。
          与志同道合的兄弟共同成长，解锁你的全部潜能。
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/about#contact"
            className="w-full sm:w-auto px-8 py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold text-base hover:bg-primary-400 transition-all whitespace-nowrap"
          >
            立即加入男人团
          </Link>
          <Link
            to="/category/self-growth"
            className="w-full sm:w-auto px-8 py-3.5 rounded-full border border-white/30 text-white font-medium text-base hover:bg-white/10 transition-all whitespace-nowrap"
          >
            探索内容
          </Link>
        </div>

        <div className="mt-16 flex items-center justify-center gap-8 md:gap-12">
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-heading font-bold text-white">50,000+</div>
            <div className="text-white/50 text-sm mt-1">社群成员</div>
          </div>
          <div className="w-px h-10 bg-white/20"></div>
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-heading font-bold text-white">200+</div>
            <div className="text-white/50 text-sm mt-1">原创文章</div>
          </div>
          <div className="w-px h-10 bg-white/20"></div>
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-heading font-bold text-white">98%</div>
            <div className="text-white/50 text-sm mt-1">好评率</div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <i className="ri-arrow-down-line text-white/40 text-2xl"></i>
      </div>
    </section>
  );
}

function CategorySection() {
  const colorMap: Record<string, { bg: string; text: string; border: string }> = {
    primary: { bg: 'bg-primary-100', text: 'text-primary-700', border: 'border-primary-200' },
    accent: { bg: 'bg-accent-100', text: 'text-accent-700', border: 'border-accent-200' },
    secondary: { bg: 'bg-secondary-100', text: 'text-secondary-700', border: 'border-secondary-200' },
  };

  return (
    <section className="py-20 md:py-28 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-14">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
            探索成长领域
          </h2>
          <p className="text-foreground-500 text-base md:text-lg max-w-xl mx-auto">
            四大核心板块，覆盖男性成长的每一个关键维度
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {categories.map((cat) => {
            const colors = colorMap[cat.color] || colorMap.primary;
            return (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group bg-background-50 border border-background-200/70 rounded-2xl p-6 hover:border-primary-300 transition-all duration-300"
              >
                <div className={`w-14 h-14 flex items-center justify-center rounded-xl ${colors.bg} ${colors.text} mb-5 group-hover:scale-110 transition-transform duration-300`}>
                  <i className={`${cat.icon} text-2xl`}></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                  {cat.name}
                </h3>
                <p className="text-foreground-500 text-sm leading-relaxed">
                  {cat.description}
                </p>
                <div className="mt-4 flex items-center gap-2 text-primary-600 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span>查看文章</span>
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FeaturedArticles() {
  const featured = articles.slice(0, 6);

  return (
    <section className="py-20 md:py-28 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-14 gap-4">
          <div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-3">
              精选文章
            </h2>
            <p className="text-foreground-500 text-base">
              来自社群导师和成员的干货分享
            </p>
          </div>
          <Link
            to="/category/health"
            className="text-primary-600 text-sm font-medium hover:text-primary-700 transition-colors flex items-center gap-1 whitespace-nowrap"
          >
            查看全部 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((article) => {
            const cat = categories.find((c) => c.slug === article.category);
            return (
              <Link
                key={article.id}
                to={`/article/${article.id}`}
                className="group bg-background-50 rounded-2xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    title={`男人团 - ${article.title}`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  {cat && (
                    <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-background-50/90 backdrop-blur-sm text-foreground-700 text-xs font-medium">
                      {cat.name}
                    </span>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3 line-clamp-2 group-hover:text-primary-700 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-foreground-500 text-sm leading-relaxed line-clamp-2 mb-4">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-foreground-400">
                    <span>{article.author}</span>
                    <span>{article.date}</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection() {
  return (
    <section className="py-20 md:py-28 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-14">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
            兄弟们的真实感受
          </h2>
          <p className="text-foreground-500 text-base md:text-lg max-w-xl mx-auto">
            来自社群成员的真实反馈，一起见证成长的力量
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <div
              key={i}
              className="bg-background-100 rounded-2xl p-6 border border-background-200/70"
            >
              <div className="flex items-center gap-4 mb-4">
                <img
                  src={t.avatar}
                  alt={t.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <div className="font-semibold text-foreground-900 text-sm">
                    {t.name}
                  </div>
                  <div className="text-foreground-400 text-xs">
                    {t.age}岁 · {t.role}
                  </div>
                </div>
              </div>
              <p className="text-foreground-600 text-sm leading-relaxed">
                "{t.content}"
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="py-20 md:py-28 bg-background-100">
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <div className="text-center mb-14">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
            常见问题
          </h2>
          <p className="text-foreground-500 text-base">
            关于男人团，你想知道的都在这里
          </p>
        </div>

        <div className="flex flex-col gap-3">
          {faqs.map((faq, i) => (
            <div
              key={i}
              className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden"
            >
              <button
                className="w-full px-6 py-5 flex items-center justify-between text-left gap-4"
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
              >
                <h3 className="font-heading text-foreground-900 font-medium text-base">
                  {faq.question}
                </h3>
                <i
                  className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-300 text-lg ${
                    openIndex === i ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>
              {openIndex === i && (
                <div className="px-6 pb-5">
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="relative py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20atmospheric%20abstract%20background%20with%20golden%20amber%20accents%2C%20masculine%20community%20theme%2C%20elegant%20gradient%2C%20dramatic%20lighting%2C%20inspirational%20motivation%20atmosphere%2C%20no%20text&width=1920&height=600&seq=home-cta-bg&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-top"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80"></div>

      <div className="relative z-10 max-w-2xl mx-auto px-4 text-center">
        <h2 className="font-heading text-3xl md:text-5xl font-bold text-white mb-5">
          准备好成为更好的自己了吗？
        </h2>
        <p className="text-white/70 text-lg mb-10 leading-relaxed">
          加入男人团，与 <strong className="text-white">50,000+</strong> 兄弟一起，
          突破自我界限，解锁男性潜能。
        </p>
        <Link
          to="/about#contact"
          className="inline-block px-10 py-4 rounded-full bg-primary-500 text-background-50 font-semibold text-lg hover:bg-primary-400 transition-all whitespace-nowrap"
        >
          立即加入
        </Link>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div className="min-h-screen bg-background-50">
      <HomeSchema />
      <Navbar />
      <main>
        <Hero />
        <CategorySection />
        <FeaturedArticles />
        <TestimonialsSection />
        <FAQSection />
        <CTASection />
      </main>
      <Footer />
      <ScrollToTop />
    </div>
  );
}